import { __decorate } from "tslib";
import __NG_CLI_RESOURCE__0 from "./top-nav.component.html?ngResource";
import { CommonModule } from '@angular/common';
import { Component, Inject, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import logo from 'Images/epic-rv-logo.svg';
import cortexLogoLight from 'Images/cortex-logo-light.svg';
import cortexLogoDark from 'Images/cortex-logo-dark.svg';
import cortexBrain from 'Images/cortex-brain.png';
import { PaymentDueNoticeComponent } from '../payment-due-notice/payment-due-notice.component';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';
import { CortexAppsService } from '../../services/cortex-apps.service';
import startCase from 'lodash/startCase';
// ─── App-switcher tile sizing ──────────────────────────────
// The switcher panel renders each app as a 44×44 colored tile
// (`.app-switcher-badge` in top-nav.component.scss) with 2–3
// letter initials. Maximum font sizes that visually fit inside
// that tile, keyed by initials length. Eyeballed against the
// common Cortex badges (CX, DM, RV, LPR, SOC, WW, …).
const BADGE_FIT_CAP_2_LETTERS = 24;
const BADGE_FIT_CAP_3_LETTERS = 18;
const BADGE_FIT_CAP_LONGER = 14;
// Substrings hidden from the app-switcher dropdown. Substring
// match (not exact) so env-prefixed/suffixed variants (e.g.
// `remotevision-dev`, `cortex-stg`, `nexus-stg`) are caught too —
// `nexus` is still listed because legacy app slugs from the
// pre-rename Nexus-era seed survive in some environments.
const APP_SWITCHER_HIDDEN_SLUG_SUBSTRINGS = ['remotevision', 'cortex', 'nexus'];
/**
 * Build the "Manage account" URL for the top-nav by appending
 * ?noredirect=1 to the Cortex UI base URL. Uses URL() so any
 * existing query/hash on the base URL is preserved, and an
 * existing `noredirect` parameter is overwritten.
 *
 * Returns '' for empty input, or the original input if URL parsing
 * fails (config bug — keep the link working without the param).
 *
 * @param {string} uiBaseUrl Base URL of the Cortex UI (e.g. https://cortex.epicio.com).
 * @returns {string} URL with `?noredirect=1` appended, or a safe fallback string.
 */
export function buildCortexManageAccountUrl(uiBaseUrl) {
    if (!uiBaseUrl) {
        return '';
    }
    try {
        const url = new URL(uiBaseUrl);
        url.searchParams.set('noredirect', '1');
        return url.toString();
    }
    catch {
        return uiBaseUrl;
    }
}
/** @deprecated Use `buildCortexManageAccountUrl`. Retained for in-flight imports. */
export const buildNexusManageAccountUrl = buildCortexManageAccountUrl;
/**
 * Whether an app-switcher entry should render its uploaded icon image
 * (badgeMode === 'icon' with iconData present) instead of the initials
 * badge. Mirrors the cortex-platform ui-kit app-switcher behavior.
 *
 * @param {object} app App-switcher entry to evaluate.
 * @param {string} [app.badgeMode] Badge rendering mode; 'icon' selects the image.
 * @param {string|null} [app.iconData] Data-URI of the uploaded icon image.
 * @returns {boolean} True when the entry should render its icon image instead of initials.
 */
export function shouldRenderAppIcon(app) {
    return app.badgeMode === 'icon' && Boolean(app.iconData);
}
let TopNavComponent = class TopNavComponent {
    modalService;
    cortexAppsService;
    cdr;
    userName;
    // TODO: fix this before deploying again
    isDev = false; // Boolean(!window.PRODUCTION);
    userInitials = '';
    showPaymentDue = false;
    companyTooltip = '';
    user = {};
    userMenuItems = [
        { label: 'Profile', icon: 'far fa-user', command: () => this.openProfile() },
        { label: 'Logout', icon: 'fas fa-sign-out-alt', command: () => this.logout() }
    ];
    logoSrc = logo;
    cortexLogoLightSrc = cortexLogoLight;
    cortexLogoDarkSrc = cortexLogoDark;
    cortexBrainSrc = cortexBrain;
    // Exposed to the template so the app-switcher can pick icon vs. initials.
    shouldRenderAppIcon = shouldRenderAppIcon;
    // Humanizes single-token camelCase names ("WirelessWindow" ->
    // "Wireless Window") so the label matches Cortex's app-switcher and
    // wraps on real word boundaries instead of breaking mid-word.
    // lodash startCase splits camelCase + acronym runs ("LPRReader" ->
    // "LPR Reader") and leaves already-spaced names unchanged.
    humanizeAppName = startCase;
    // Cortex app-switcher state
    cortexAuthenticated = false;
    cortexUiUrl = '';
    cortexBridgeUrl = '';
    cortexManageAccountUrl = '';
    cortexApps = [];
    appsLoading = false;
    // Operator-side toggle from rv-api /admin/config → appConfig.cortex.appSwitcher.
    // Hides the top-nav grid icon unless rv-api explicitly returns
    // `true` for this flag. Fail-closed so the icon stays hidden in
    // any environment that hasn't deployed an rv-api version aware
    // of the flag — protects production from accidental UI leakage
    // during a partial rollout.
    appSwitcherEnabled = false;
    appsLoaded = false;
    // Cached AngularJS $state to avoid repeated injector lookups on each navigation
    angularJsState;
    constructor(modalService, cortexAppsService, cdr) {
        this.modalService = modalService;
        this.cortexAppsService = cortexAppsService;
        this.cdr = cdr;
        const appConfig = window.appConfig;
        this.user = appConfig?.user || {};
        // rv-api emits `appConfig.cortex` whenever the cortex feature is
        // enabled. The legacy `appConfig.nexus` alias has been retired —
        // rv-api no longer emits it.
        const cortexConfig = appConfig?.cortex;
        if (cortexConfig?.authenticated) {
            this.cortexAuthenticated = true;
        }
        this.cortexUiUrl = cortexConfig?.uiBaseUrl || '';
        this.cortexBridgeUrl = cortexConfig?.bridgeUrl || '';
        // "Manage account" sends the user to Cortex to manage profile/security.
        // ?noredirect=1 keeps them in Cortex instead of bouncing back via
        // auto-redirect (e.g., users with a single-app default would otherwise
        // land right back here). Honored by cortex-ui's landingGuard.
        this.cortexManageAccountUrl = buildCortexManageAccountUrl(this.cortexUiUrl);
        // Only show the icon when rv-api explicitly returns `true`. Older
        // rv-api versions that don't surface the flag (or partial deploys
        // where rv-api hasn't picked up the new ansible config yet) leave
        // it undefined → stays hidden. Prod stays clean until both sides
        // are deployed and the flag is flipped on.
        this.appSwitcherEnabled = cortexConfig?.appSwitcher === true;
        this.userName = this.user.name || this.user.email || 'User';
        this.userInitials = (this.userName || '')
            .split(' ')
            .map((part) => part[0])
            .join('')
            .substring(0, 2)
            .toUpperCase();
        // Determine payment due visibility from user org settings
        this.showPaymentDue = Boolean(this.user.org?.settings?.payment?.dueNotice);
        const appV = appConfig?.appVersion ?? '';
        const apiV = appConfig?.pc3Version ?? '';
        this.companyTooltip = `APP: ${appV}\n API: v${apiV}`;
        const ng = window.angular;
        const injector = ng?.element?.(document.body)?.injector?.();
        const $state = (injector?.get &&
            injector.get('$state'));
        if ($state?.go) {
            this.angularJsState = $state;
        }
    }
    openQuickFind(event) {
        if (this.modalService?.quickfind) {
            this.modalService.quickfind(event);
        }
    }
    toggleSidebar() {
        // Dispatch custom event to communicate with side-nav component
        window.dispatchEvent(new CustomEvent('toggle-mobile-sidebar'));
    }
    toggleDesktopSidebar() {
        // Dispatch custom event to toggle desktop sidebar collapse
        window.dispatchEvent(new CustomEvent('toggle-desktop-sidebar'));
    }
    openKnowledgeBase() {
        window.open('https://epic-io.atlassian.net/wiki/x/1gGYIgM', '_blank');
    }
    openNotifications() {
        // Trigger Beamer notifications (third-party service)
        const beamer = window.Beamer;
        if (beamer && typeof beamer.show === 'function') {
            try {
                beamer.show();
            }
            catch (error) {
                console.warn('Beamer notifications not available:', error);
                // Fallback: could show a simple alert or modal
            }
        }
        else {
            console.warn('Beamer is not initialized or not available');
        }
    }
    isDarkMode = false;
    toggleTheme() {
        this.isDarkMode = !this.isDarkMode;
        document.body.classList.toggle('dark-mode', this.isDarkMode);
        localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light');
    }
    ngOnInit() {
        // Initialize theme based on saved preference or system setting
        const savedTheme = localStorage.getItem('theme');
        this.isDarkMode = savedTheme ? savedTheme === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
        document.body.classList.toggle('dark-mode', this.isDarkMode);
    }
    navigateToState(state, fallbackUrl) {
        // Prefer SPA navigation using cached AngularJS ui-router
        if (this.angularJsState?.go) {
            this.angularJsState.go(state);
            return;
        }
        // Fallback to pushState to avoid full reload
        window.history.pushState({}, '', fallbackUrl);
        window.dispatchEvent(new Event('locationchange'));
    }
    openProfile() {
        this.navigateToState('user profile', '/profile/user');
    }
    logout() {
        const appConfig = window.appConfig;
        const redirectUrl = encodeURIComponent(window.location?.href || '/');
        const target = `${appConfig?.apiBaseUrl || ''}/account/logout/?redirectUrl=${redirectUrl}`;
        window.location.href = target;
    }
    goHome() {
        this.navigateToState('app', '/');
    }
    loadCortexApps() {
        if (this.appsLoaded || !this.cortexAuthenticated) {
            return;
        }
        this.appsLoading = true;
        this.cdr.markForCheck();
        this.cortexAppsService.getApps().subscribe({
            next: (apps) => {
                // See APP_SWITCHER_HIDDEN_SLUG_SUBSTRINGS — hides the current
                // app from its own switcher and the Cortex Portal entry
                // itself (Cortex is the SSO provider, not a user-facing
                // product; reachable via "Manage account" in the panel footer).
                this.cortexApps = apps.filter(app => !APP_SWITCHER_HIDDEN_SLUG_SUBSTRINGS.some(sub => app.slug.includes(sub)));
                this.appsLoading = false;
                this.appsLoaded = true;
                this.cdr.markForCheck();
            },
            error: () => {
                // Transient failures (network blip, brief 5xx) shouldn't strand
                // the switcher in an empty state for the rest of the session.
                // Leave `appsLoaded` false so reopening the panel re-attempts
                // the fetch — the service has already poisoned its cache on
                // error, so the next subscribe builds a fresh request.
                this.cortexApps = [];
                this.appsLoading = false;
                this.cdr.markForCheck();
            },
        });
    }
    signInToCortex() {
        // Bridge URL drives the OIDC sign-in flow and returns the browser to
        // RV with a session established (sets req.session.cortexOrigin via
        // /auth/internal/establish-session). Falling back to the bare Cortex
        // UI URL only opens the Cortex portal in a new tab without completing
        // the round-trip into RV — useful as a last-ditch when the bridge is
        // missing, but useless on its own for re-authenticating the current
        // RV session.
        const target = this.cortexBridgeUrl || this.cortexUiUrl;
        if (target) {
            window.location.href = target;
        }
    }
    /**
     * Pick a badge font size for the 44x44 grid tile
     * (`.app-switcher-badge` in top-nav.component.scss).
     *
     * Rule: smaller of (a) Cortex's explicit `badgeFontSize` when
     * present, and (b) what actually fits the tile given the
     * initials length (see BADGE_FIT_CAP_* constants).
     * - env seeds with a sensible size (e.g. 20 for "DM") → used as-is
     * - env seeds with an oversized value (staging has 30 for every app) → capped
     * - no DB value at all → uses the length-based cap directly
     *
     * @param {CortexAppEntry} app Cortex app whose badge font size is being computed.
     * @returns {number} Font size in pixels for the tile's initials label.
     */
    badgeFontSizeFor(app) {
        const len = (app.badgeInitials || '').length;
        const fitCap = len <= 2 ? BADGE_FIT_CAP_2_LETTERS
            : len === 3 ? BADGE_FIT_CAP_3_LETTERS
                : BADGE_FIT_CAP_LONGER;
        if (typeof app.badgeFontSize === 'number' && app.badgeFontSize > 0) {
            return Math.min(app.badgeFontSize, fitCap);
        }
        return fitCap;
    }
    static ctorParameters = () => [
        { type: undefined, decorators: [{ type: Inject, args: ['ModalService',] }] },
        { type: CortexAppsService },
        { type: ChangeDetectorRef }
    ];
};
TopNavComponent = __decorate([
    Component({
        selector: 'topnav',
        template: __NG_CLI_RESOURCE__0,
        standalone: true,
        imports: [
            CommonModule,
            PaymentDueNoticeComponent,
            MenuModule,
            AvatarModule,
            FormsModule,
            ButtonModule,
            OverlayPanelModule,
            // Standalone components must explicitly import every directive
            // the template uses. The legacy NgModule (`ng-app/app.module.ts`)
            // also imports `TooltipModule`, which made the template compile
            // for non-standalone callers — but as a `standalone: true`
            // component this needs it locally too. Without this import the
            // `pTooltip` attributes throughout the template are inert
            // (no error, no tooltip).
            TooltipModule,
        ],
        encapsulation: ViewEncapsulation.None,
        changeDetection: ChangeDetectionStrategy.OnPush,
    })
], TopNavComponent);
export { TopNavComponent };
